import { ChevronRight } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router";
import type { Frist } from "../api/typen";
import { betrag } from "../format";
import { diagrammText, nochOffen, saeulen } from "./lage";
import { FRISTEN_TEXTE, listeOffen } from "./saetze";
import { Saeulen } from "./Saeulen";
import { ersteSaeule, type ModulAuftritt, ZAEHLEN_NACH_SAEULE } from "./sichtbarkeit";
import { Zahl } from "./Zahl";
import { Zustand } from "./Zustand";

/** Fristen-Modul der Startseite (DESIGN-W82 „Fristen-Modul“, E10, E11): Kopf „Fristen“ mit Untertitel und der offenen
 *  Summe „noch offen“ (rollt bei jeder Änderung, zählt beim Auftritt hoch), die Säulen mit Textfassung für Vorlesen, die
 *  Legende und die Liste nach D-333 (`fristenBald`, Zeilen kommen vom Aufrufer) mit Kopf „Fristen“, „4 offen“ und
 *  „Alle“ (öffnet die Fristen-Ansicht als Unterseite). Kennung des Abschnitts wie bisher: `section[aria-labelledby=fristen]`.
 *  FW21: beim vollen Auftritt (lebhaft) spielen Formen, Säulen und Summe erst, wenn das Modul zu 60 % im Bild ist
 *  (`useModulAuftritt`); solange es wartet, trägt der Abschnitt `data-wartet`, danach `data-choreo` mit `--modul-t`. */
export function FristenModul(props: {
  /** alle Fristen des Fensters, auch erledigte (`useFristen(false)`); fehlen sie noch, die offenen */
  fristen: Frist[];
  heute: string;
  /** Zahl der offenen Fristen der Liste (Kopf „4 offen“) */
  offen: number;
  /** die Zeilen der Liste (`FristZeile` je Frist aus `fristenBald`) */
  zeilen: ReactNode;
  /** Choreografie beim vollen Auftritt (`useModulAuftritt`); ohne sie steht alles sofort */
  auftritt?: ModulAuftritt;
}) {
  const spalten = saeulen(props.fristen, props.heute);
  const summe = nochOffen(spalten);
  const fensterLeer = spalten.every((s) => s.segmente.length === 0);
  const kopf = listeOffen(props.offen);
  const a = props.auftritt;
  const laeuft = a?.zustand === "laeuft";
  return (
    <section
      ref={a?.ref}
      className="fristen-modul glas glas-modul"
      aria-labelledby="fristen"
      data-testid="fristen-modul"
      data-wartet={a?.zustand === "wartet" ? "" : undefined}
      data-choreo={laeuft ? "" : undefined}
      style={laeuft ? ({ "--modul-t": `${a.t}ms` } as React.CSSProperties) : undefined}
    >
      <div className="fm-kopf">
        <h2 id="fristen" className="fm-titel w-modul-titel">
          {FRISTEN_TEXTE.titel}
          <span className="fm-untertitel w-untertitel">{FRISTEN_TEXTE.untertitel}</span>
        </h2>
        <p className="fm-summe">
          <b className="w-summe">
            <Zahl
              wert={summe}
              text={betrag(summe)}
              hochzaehlen={a !== undefined && a.zustand !== "fertig"}
              warten={!laeuft}
              verzug={ZAEHLEN_NACH_SAEULE}
              takt={(el) => ersteSaeule(el, a?.t ?? 0)}
              format={(n) => betrag(Math.round(n * 100) / 100)}
              kennung="noch-offen"
            />
          </b>
          <span className="w-legende">{FRISTEN_TEXTE.nochOffen}</span>
        </p>
      </div>
      {fensterLeer ? (
        <p className="fm-leer w-lesetext">{FRISTEN_TEXTE.leer}</p>
      ) : (
        <>
          <Saeulen spalten={spalten} />
          <p className="sr-only" data-testid="diagramm-text">
            {diagrammText(spalten)}
          </p>
          <p className="legende w-legende" aria-hidden="true">
            <span>
              <Zustand form="raute" />
              {FRISTEN_TEXTE.legende.ueberfaellig}
            </span>
            <span>
              <Zustand form="punkt" />
              {FRISTEN_TEXTE.legende.bald}
            </span>
            <span>
              <Zustand form="ring" />
              {FRISTEN_TEXTE.legende.spaeter}
            </span>
            <span data-erledigt="">
              <Zustand form="punkt" />
              {FRISTEN_TEXTE.legende.erledigt}
            </span>
          </p>
        </>
      )}
      {/* N3 (B9, R11 `.liste-kopf`): sichtbar nur „4 offen“, „Fristen,“ steht im Lesetext; „Alle“ mit Pfeil */}
      <div className="fm-liste-kopf">
        <h3 className="fm-liste-titel w-untertitel" aria-label={kopf.lesetext}>
          {kopf.text}
        </h3>
        <Link to="/fristen" className="fm-alle w-knopf-klein" aria-label={FRISTEN_TEXTE.alleLesetext}>
          {FRISTEN_TEXTE.alle}
          <ChevronRight size={18} strokeWidth={2} aria-hidden />
        </Link>
      </div>
      <ul className="fm-liste" data-testid="fristen-liste">
        {props.offen === 0 ? <li className="fm-liste-leer w-lesetext">{FRISTEN_TEXTE.listeLeer}</li> : props.zeilen}
      </ul>
    </section>
  );
}
